import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiZap, FiArrowRight, FiLock, FiShield, FiGlobe } from 'react-icons/fi';
import '../styles/HomeHero.css';

/* ─── Payroll calculation helpers ──────────────────────────────────────────
   All figures use 2025/26 HMRC thresholds and UK auto-enrolment minimums.
   These run on the client on every render — no server, no stale data.
────────────────────────────────────────────────────────────────────────── */

const AVG_MONTHLY_GROSS = 2_333;         // ~£28k/yr, UK median full-time
const AUTO_ENROL_LEL    = 533;           // Lower Earnings Limit 2025/26 per month
const AUTO_ENROL_RATE   = 0.08;          // 5% employee + 3% employer minimum

/** Total gross payroll for the month */
const calcPayroll = (employees) =>
  employees * AVG_MONTHLY_GROSS;

/** Total pension contributions (employer + employee) on qualifying earnings */
const calcPension = (employees) => {
  const qualifying = AVG_MONTHLY_GROSS - AUTO_ENROL_LEL;
  return Math.round(employees * qualifying * AUTO_ENROL_RATE);
};

/** Format as £X,XXX with no decimal */
const fmtGBP = (n) =>
  '£' + Math.round(n).toLocaleString('en-GB');

/** Format as "D Mon YYYY" e.g. "19 Sep 2026" */
const fmtDate = (date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * HMRC P32 payment deadline:
 * Tax month runs 6th → 5th. The payment deadline is the 19th of the
 * calendar month *after* the tax month ends (22nd for electronic payment,
 * but 19th is the postal/cheque deadline and the more conservative figure).
 */
const getFilingDeadline = (now = new Date()) => {
  // Determine which tax month we are currently in
  const taxMonthStart = now.getDate() >= 6
    ? new Date(now.getFullYear(), now.getMonth(), 6)
    : new Date(now.getFullYear(), now.getMonth() - 1, 6);

  // Tax month ends on the 5th of the following calendar month
  const taxMonthEnd = new Date(
    taxMonthStart.getFullYear(),
    taxMonthStart.getMonth() + 1,
    5,
  );

  // Deadline is 19th of the calendar month after tax month ends
  const deadline = new Date(
    taxMonthEnd.getFullYear(),
    taxMonthEnd.getMonth() + 1,
    19,
  );

  const daysUntil = Math.ceil((deadline - now) / (1_000 * 60 * 60 * 24));

  return { deadline, daysUntil };
};

/* ─── Component ──────────────────────────────────────────────────────────── */

const HomeHero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [employees, setEmployees] = useState(72);   // mid-range of 45–100

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // All derived values — recalculate whenever employee count changes
  const { deadline, daysUntil } = useMemo(() => getFilingDeadline(), []);
  const payroll = useMemo(() => calcPayroll(employees), [employees]);
  const pension = useMemo(() => calcPension(employees), [employees]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const ledgerRows = [
    {
      label: 'PAYE submitted',
      meta: 'RTI · on time',
      value: `${employees} employees`,
    },
    {
      label: 'Pension contributions',
      meta: 'auto-enrolled',
      value: fmtGBP(pension),
    },
    {
      label: 'Next filing due',
      meta: `${daysUntil} days`,
      value: fmtDate(deadline),
    },
  ];

  return (
    <section className="home-hero-section">
      <div
        className="home-hero-field"
        style={{
          backgroundPosition: `${50 + mousePosition.x * 0.01}% ${50 + mousePosition.y * 0.01}%`
        }}
      />

      <div className="container">
        <motion.div
          className="home-hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* ========== TEXT BLOCK ========== */}
          <motion.div className="home-hero-text-block" variants={itemVariants}>
            <motion.div className="home-hero-eyebrow" variants={itemVariants}>
              Payroll, invoicing, accounts and HR — one login
            </motion.div>

            <motion.h1 className="home-hero-title" variants={itemVariants}>
              Run your whole business,
              <br />
              not just payroll.
            </motion.h1>

            <motion.p className="home-hero-subtitle" variants={itemVariants}>
              Secure, compliant payroll — plus invoicing, accounts payable, banking, accounting, VAT, and HR — all built to remove the manual work, not just speed it up.
            </motion.p>

            <motion.div className="home-hero-cta-buttons" variants={itemVariants}>
              <Link to="/demo" className="btn btn-primary">
                <FiZap size={18} />
                Get started free
              </Link>
              <Link to="/about" className="btn btn-secondary">
                Learn more <FiArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div className="home-hero-trust-row" variants={itemVariants}>
              <span className="home-hero-trust-item">
                <FiLock size={15} /> ISO 27001
              </span>
              <span className="home-hero-trust-item">
                <FiShield size={15} /> GDPR
              </span>
              <span className="home-hero-trust-item">
                <FiGlobe size={15} /> HMRC approved
              </span>
            </motion.div>
          </motion.div>

          {/* ========== SIGNATURE: LIVE LEDGER ========== */}
          <motion.div
            className="home-hero-ledger-block"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <div className="home-hero-ledger">
              <div className="home-hero-ledger-header">
                <span className="home-hero-ledger-title">This month, at a glance</span>
                <span className="home-hero-ledger-status">
                  <span className="home-hero-status-dot" />
                  Live
                </span>
              </div>

              <div className="home-hero-ledger-rows">
                {ledgerRows.map((row, idx) => (
                  <motion.div
                    className="home-hero-ledger-row"
                    key={row.label}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                  >
                    <div className="home-hero-row-text">
                      <span className="home-hero-row-label">{row.label}</span>
                      <span className="home-hero-row-meta">{row.meta}</span>
                    </div>
                    <span className="home-hero-row-value">{row.value}</span>
                  </motion.div>
                ))}
              </div>

              {/* Employee count slider */}
              <div className="home-hero-ledger-slider">
                <div className="home-hero-slider-label">
                  <span>Employees</span>
                  <span className="home-hero-slider-count">{employees}</span>
                </div>
                <input
                  type="range"
                  min={25}
                  max={100}
                  value={employees}
                  onChange={(e) => setEmployees(Number(e.target.value))}
                  className="home-hero-slider"
                  aria-label="Adjust employee count"
                />
                <div className="home-hero-slider-range">
                  <span>25</span>
                  <span>100</span>
                </div>
              </div>

              <div className="home-hero-ledger-footer">
                <span>{fmtGBP(payroll)} processed this cycle</span>
                <span className="home-hero-footer-delta">+8% vs last month</span>
              </div>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default HomeHero;